import { createPortal } from "react-dom";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";

const CARRIER_HELP: Record<string, string> = {
  Verizon:
    "Submit SKU-and-SIM activation with Verizon ThingSpace and mark this ICCID activated in hub inventory.",
  Vodafone:
    "Submit a Vodafone IoT state change and mark this ICCID activated in hub inventory.",
  "AT&T":
    "Submit an AT&T Control Center state change and mark this ICCID activated in hub inventory.",
};

const CARRIER_CONFIG_HINT: Record<string, string> = {
  Verizon: "Verizon ThingSpace and UWS credentials are not configured on this server.",
  Vodafone: "Vodafone IoT credentials are not configured on this server.",
  "AT&T": "AT&T Control Center credentials are not configured on this server.",
};

export function SimActivateCarrierModal({
  open,
  iccid,
  carrier,
  apiReady,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  iccid: string;
  carrier: string;
  apiReady: boolean;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): JSX.Element | null {
  if (!open) {
    return null;
  }

  const help = CARRIER_HELP[carrier] ?? "Submit carrier API activation for this ICCID.";
  const configHint =
    CARRIER_CONFIG_HINT[carrier] ?? "Carrier API credentials are not configured on this server.";

  return createPortal(
    <div
      {...hubModalOverlayProps()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sim-activate-modal-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 id="sim-activate-modal-title" className="text-lg font-semibold text-slate-900">
            Activate with {carrier}
          </h2>
          <p className="mt-1 text-sm text-slate-600">{help}</p>
        </div>
        <div className="space-y-3 px-4 py-4">
          <div>
            <p className="text-xs font-medium text-slate-600">ICCID</p>
            <p className="mt-1 font-mono text-sm text-slate-900">{iccid}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600">Carrier</p>
            <p className="mt-1 text-sm text-slate-900">{carrier}</p>
          </div>
          {!apiReady ? <p className="text-sm text-amber-800">{configHint}</p> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <Button type="button" variant="secondary" size="sm" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={!apiReady || pending || !iccid.trim()}
            isLoading={pending}
            onClick={onConfirm}
          >
            Activate
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
